import { Card, Chip, EmptyState, Link, Skeleton } from "@heroui/react";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { AccountBadge } from "@/src/components/AccountBadge";
import { DetailSection } from "@/src/components/DetailSection";
import { PostStatusChip } from "@/src/components/PostStatusChip";
import { useSetBreadcrumbLeaf } from "@/src/contexts/BreadcrumbContext";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import { PostActions } from "@/src/pages/PostDetail/components/PostActions";
import { PostComments } from "@/src/pages/PostDetail/components/PostComments";
import { PostLikes } from "@/src/pages/PostDetail/components/PostLikes";
import { useAdminPost } from "@/src/pages/PostDetail/hooks/useAdminPost";
import { usePostComments } from "@/src/pages/PostDetail/hooks/usePostComments";
import { usePostLikes } from "@/src/pages/PostDetail/hooks/usePostLikes";
import { ScoreBadge } from "@/src/pages/Posts/components/ScoreBadge";
import { ScoreDetails } from "@/src/pages/Posts/components/ScoreDetails";
import { usePostActions } from "@/src/pages/Posts/hooks/usePostActions";
import { parseModerationScores } from "@/src/pages/Posts/moderationScores";
import { ROUTES } from "@/src/routes/paths";

/**
 * Fiche d'une publication (`/posts/:postId`).
 *
 * ## Pourquoi une fiche, alors que la carte de la liste suffisait
 *
 * Elle ne suffisait que pour **trancher sur l'image**. Tout le reste d'une
 * publication — le fil des commentaires, qui l'a aimée, combien de fois elle a
 * été signalée, qui a rendu la décision précédente — n'existait nulle part dans
 * l'admin. C'est ce que les routes du 2 septembre ont débloqué :
 * `GET /moderation/post/{id}`, `…/comment` et `…/like`.
 *
 * ⚠️ **`GET /post/{id}` ne conviendrait pas** : `canViewPost` n'y fait aucune
 * exception de rôle, et répond 403 sur une publication en cours de classement,
 * retenue, refusée ou retirée par son auteur — c'est-à-dire précisément celles
 * qu'on vient lire ici.
 *
 * ⚠️ **Effacer navigue vers la liste** (`replace: true`), sinon l'écran
 * afficherait une publication disparue puis basculerait sur son propre
 * « introuvable », ce qui se lit comme une panne alors que c'est le résultat
 * demandé. Même parti que la fiche d'un événement.
 */
export function PostDetail() {
  const { postId } = useParams();
  const t = useTranslations("posts");
  const ta = useTranslations("a11y");
  const { locale } = useLocale();
  const navigate = useNavigate();

  // `Number(undefined)` vaut `NaN`, que le serveur refuserait en 400. Une URL
  // sans nombre est traitée ici comme une publication introuvable, ce qu'elle
  // est.
  const parsed = Number(postId);
  const isValidId = Number.isInteger(parsed) && parsed > 0;
  const id = isValidId ? parsed : 0;

  const { post, isLoading, isMissing, isError } = useAdminPost(id);

  const [commentPage, setCommentPage] = useState(1);
  const [likePage, setLikePage] = useState(1);
  // Les cœurs ne se chargent qu'à l'ouverture de la section : une publication
  // populaire en porte des milliers, et on ouvre le plus souvent cette fiche
  // pour lire un commentaire.
  const [showLikes, setShowLikes] = useState(false);

  const comments = usePostComments(id, commentPage);
  const likes = usePostLikes(id, likePage, showLikes);

  const {
    approve,
    reject,
    destroy,
    destroyComment,
    pendingId,
    pendingCommentId,
  } = usePostActions();

  /*
    `AccountBadge` attend la forme des identités de modération ; l'auteur d'un
    post porte les mêmes champs sous d'autres noms. La conversion est mémoïsée —
    sans quoi `react-perf` y voit un objet reconstruit à chaque rendu — et se
    fait **avant les retours anticipés** : un hook n'a pas le droit d'être
    appelé sous condition.
  */
  const auteur = useMemo(
    () =>
      post === undefined
        ? null
        : {
            id: post.author.accountId,
            username: post.author.username,
            avatarUrl: post.author.avatar,
          },
    [post],
  );

  // « Fil > Publication n° 42 » : l'en-tête ne peut pas résoudre l'identifiant
  // de l'URL, seul cet écran sait de quoi il parle.
  useSetBreadcrumbLeaf(
    post === undefined ? undefined : t("post-title", { id: post.id }),
  );

  if (!isValidId || isMissing || (isError && !post)) {
    return (
      <Card className="mx-auto w-full max-w-4xl p-10">
        <Card.Content>
          <EmptyState className="flex flex-col items-center gap-2 text-center">
            <p className="text-base font-medium">{t("missing-title")}</p>
            <p className="max-w-md text-pretty text-muted">
              {t("missing-description")}
            </p>
            <Link className="pt-2 text-sm" href={ROUTES.posts}>
              {t("back-to-list")}
            </Link>
          </EmptyState>
        </Card.Content>
      </Card>
    );
  }

  if (isLoading || !post) {
    return (
      <div aria-hidden className="mx-auto flex w-full max-w-4xl flex-col gap-4">
        <Skeleton className="h-64 w-full rounded-lg" />
        <Skeleton className="h-28 w-full rounded-lg" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </div>
    );
  }

  const scores = parseModerationScores(post.moderationScores);

  /*
    ⚠️ **Effacer navigue vers la liste**, et le fait ici plutôt que dans le
    hook : `usePostActions` sert aussi la liste, où il n'y a nulle part où
    aller. Sans ce départ, la fiche afficherait une publication disparue puis
    basculerait sur son propre « introuvable » — ce qui se lit comme une panne
    alors que c'est le résultat demandé.
  */
  const destroyAndLeave = (cible: number) =>
    destroy(cible, {
      onSuccess: () => navigate(ROUTES.posts, { replace: true }),
    });

  const publie = new Intl.DateTimeFormat(locale, {
    dateStyle: "long",
    timeStyle: "short",
  }).format(new Date(post.createdAt));

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4">
      <Card className="flex flex-col gap-4 p-5">
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold tracking-tight">
              {t("post-title", { id: post.id })}
            </h2>

            <PostStatusChip status={post.moderationStatus} />

            {/* ⚠️ **Retirée par son auteur n'est pas un statut** : la
                publication garde le sien, elle a seulement quitté le fil sans
                que la modération l'ait décidé. La ligne survit avec son contenu,
                et c'est exprès. */}
            {post.deletedAt ? (
              <Chip color="default" size="sm" variant="secondary">
                {t("deleted-by-author")}
              </Chip>
            ) : null}

            <Chip size="sm" variant="secondary">
              {post.type === "gallery" ? t("type-gallery") : t("type-thread")}
            </Chip>

            {/* Le nom du jeu est résolu **par le serveur** sur cette route : une
                requête de moins, et pas de table de catégories écrite ici. */}
            {post.category ? (
              <Chip size="sm" variant="secondary">
                {post.category.name}
              </Chip>
            ) : null}

            <ScoreBadge scores={scores} />
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
            <span>{t("published-by")}</span>
            <AccountBadge account={auteur} isLinked />
            <span>{publie}</span>
          </div>

          {post.content ? (
            <p className="text-sm text-pretty whitespace-pre-wrap">
              {post.content}
            </p>
          ) : (
            // Une galerie n'a pas forcément de texte : ce sont les photos qui
            // portent la publication. Le dire évite de croire à un chargement
            // manquant.
            <p className="text-sm text-muted italic">{t("no-content")}</p>
          )}
        </div>

        {/* Les photos en grand : sur cette fiche on regarde l'image, la liste
            en montrait la vignette. */}
        {post.photos.length > 0 ? (
          <div className="flex flex-wrap gap-3">
            {post.photos.map((photo, rang) => (
              <a
                href={photo.url}
                key={photo.id}
                rel="noreferrer"
                target="_blank"
              >
                <img
                  alt={ta("post-photo", { position: rang + 1 })}
                  className="size-40 rounded-md object-cover"
                  loading="lazy"
                  src={photo.url}
                />
              </a>
            ))}
          </div>
        ) : null}

        <ScoreDetails photos={post.photos} scores={scores} />

        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Counter label={t("counter-likes")} value={post.likeCount} />
          <Counter label={t("counter-comments")} value={post.commentCount} />
          {/* Un dossier signalé douze fois ne se juge pas comme une publication
              retenue par le classifieur : le compte est en rouge dès qu'il
              dépasse zéro. */}
          <Counter
            isAlarming={post.reportCount > 0}
            label={t("counter-reports")}
            value={post.reportCount}
          />
          <Counter label={t("counter-photos")} value={post.photos.length} />
        </ul>
      </Card>

      {/* Les actions **avant** les commentaires : on ouvre une fiche de
          publication pour décider, pas pour lire le fil. */}
      <PostActions
        isPending={pendingId === post.id}
        onApprove={approve}
        onDelete={destroyAndLeave}
        onReject={reject}
        post={post}
      />

      <DetailSection
        count={comments.total}
        emptyLabel={t("comments-empty")}
        isEmpty={!comments.isLoading && comments.comments.length === 0}
        title={t("comments")}
      >
        {comments.isLoading ? (
          <div aria-hidden className="flex flex-col gap-2">
            {[0, 1].map((row) => (
              <Skeleton className="h-16 w-full rounded-lg" key={row} />
            ))}
          </div>
        ) : (
          <PostComments
            comments={comments.comments}
            page={commentPage}
            pageCount={comments.pageCount}
            pendingCommentId={pendingCommentId}
            total={comments.total}
            onDelete={destroyComment}
            onPageChange={setCommentPage}
          />
        )}
      </DetailSection>

      <DetailSection
        count={post.likeCount}
        emptyLabel={t("likes-empty")}
        isEmpty={post.likeCount === 0}
        title={t("likes")}
      >
        {showLikes ? (
          <PostLikes
            isLoading={likes.isLoading}
            likes={likes.likes}
            page={likePage}
            pageCount={likes.pageCount}
            total={likes.total}
            onPageChange={setLikePage}
          />
        ) : (
          <button
            className="link w-fit text-sm"
            type="button"
            onClick={() => setShowLikes(true)}
          >
            {t("likes-show", { count: post.likeCount })}
          </button>
        )}
      </DetailSection>
    </div>
  );
}

function Counter({
  label,
  value,
  isAlarming = false,
}: {
  label: string;
  value: number;
  isAlarming?: boolean;
}) {
  return (
    <li className="flex flex-col items-center rounded-lg bg-default px-2 py-3 text-default-foreground">
      <span
        className={`text-lg font-semibold tabular-nums ${
          isAlarming ? "text-danger" : ""
        }`}
      >
        {value}
      </span>
      <span className="text-center text-xs text-muted">{label}</span>
    </li>
  );
}
